import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { pagesService } from '../../../lib/services';
import { supabase } from '../../../lib/supabase';

const LLMTxtGenerator = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [llmContent, setLlmContent] = useState('');
  const [generated, setGenerated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('generator');

  const [config, setConfig] = useState({
    siteName: 'ContentCraft CMS',
    siteDescription: 'A professional content management platform for marketing teams and business owners.',
    siteUrl: window.location?.origin,
    language: 'en',
    includePages: true,
    includeSitemap: true,
    includeContact: false,
    contactEmail: '',
    allowTraining: true,
    allowIndexing: true,
    preferredCitation: 'Please cite as: [Page Title] from ContentCraft CMS',
    customInstructions: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data: pagesData } = await pagesService?.getAll({ status: 'published' });
    setPages(pagesData || []);

    try {
      const { data } = await supabase?.from('seo_tools_config')?.select('config_value')?.eq('config_key', 'llm_txt_config')?.maybeSingle();
      if (data?.config_value) {
        try {
          const parsed = JSON.parse(data?.config_value);
          setConfig(prev => ({ ...prev, ...parsed }));
        } catch (e) {}
      }
    } catch (e) {}
    setLoading(false);
  };

  const generateLlmTxt = async () => {
    setGenerating(true);
    const siteUrl = config?.siteUrl || window.location?.origin;
    const now = new Date()?.toISOString()?.split('T')?.[0];

    const lines = [];

    // Header
    lines?.push(`# ${config?.siteName}`);
    lines?.push('');
    lines?.push(`> ${config?.siteDescription}`);
    lines?.push('');

    // Metadata
    lines?.push('## Site Information');
    lines?.push('');
    lines?.push(`- **URL**: ${siteUrl}`);
    lines?.push(`- **Language**: ${config?.language}`);
    lines?.push(`- **Last Updated**: ${now}`);
    if (config?.includeContact && config?.contactEmail) {
      lines?.push(`- **Contact**: ${config?.contactEmail}`);
    }
    lines?.push('');

    // AI Usage Policy
    lines?.push('## AI Usage Policy');
    lines?.push('');
    lines?.push(`- **Training**: ${config?.allowTraining ? 'Permitted' : 'Not permitted'}`);
    lines?.push(`- **Indexing for AI search**: ${config?.allowIndexing ? 'Permitted' : 'Not permitted'}`);
    if (config?.preferredCitation) {
      lines?.push(`- **Citation preference**: ${config?.preferredCitation}`);
    }
    lines?.push('');

    // Pages
    if (config?.includePages && pages?.length > 0) {
      lines?.push('## Content Index');
      lines?.push('');
      lines?.push('The following pages are available on this site:');
      lines?.push('');
      pages?.forEach(page => {
        const slug = page?.slug === 'home' || page?.slug === '/' ? '' : `/${page?.slug}`;
        const url = `${siteUrl}${slug}`;
        lines?.push(`### ${page?.title}`);
        lines?.push('');
        lines?.push(`- **URL**: ${url}`);
        if (page?.updatedAt) {
          lines?.push(`- **Last Modified**: ${new Date(page.updatedAt)?.toISOString()?.split('T')?.[0]}`);
        }
        lines?.push('');
      });
    }

    // Sitemap reference
    if (config?.includeSitemap) {
      lines?.push('## Sitemap');
      lines?.push('');
      lines?.push(`Full sitemap available at: ${siteUrl}/sitemap.xml`);
      lines?.push('');
    }

    // Custom instructions
    if (config?.customInstructions) {
      lines?.push('## Additional Instructions for AI');
      lines?.push('');
      lines?.push(config?.customInstructions);
      lines?.push('');
    }

    // Footer
    lines?.push('---');
    lines?.push(`*Generated by ContentCraft CMS on ${now}*`);

    setLlmContent(lines?.join('\n'));
    setGenerated(true);
    setGenerating(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await supabase?.from('seo_tools_config')?.upsert(
        { config_key: 'llm_txt_config', config_value: JSON.stringify(config), updated_at: new Date()?.toISOString() },
        { onConflict: 'config_key' }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Save error:', e);
    }
    setSaving(false);
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(llmContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([llmContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'llm.txt';
    a?.click();
    URL.revokeObjectURL(url);
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setGenerated(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Icon name="Bot" size={20} className="text-violet-600" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">LLM.txt Generator</h2>
            <p className="text-sm text-gray-600">Generate llm.txt for AI engines like ChatGPT and Perplexity</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            iconName={saved ? 'Check' : 'Save'}
            iconPosition="left"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Config'}
          </Button>
          <Button
            variant="primary"
            iconName="Zap"
            iconPosition="left"
            onClick={generateLlmTxt}
            disabled={generating || loading}
          >
            {generating ? 'Generating...' : 'Generate llm.txt'}
          </Button>
        </div>
      </div>
      {/* What is llm.txt info banner */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon name="Sparkles" size={18} className="text-violet-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-violet-900">What is llm.txt?</p>
            <p className="text-xs text-violet-700 mt-1">
              llm.txt is a proposed standard (similar to robots.txt) that helps AI language models like ChatGPT, Perplexity, Claude, and Gemini understand your site's content, structure, and usage policies. It improves how AI engines cite and reference your content.
            </p>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {['generator', 'output']?.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors -mb-px ${
              activeTab === tab
                ? 'border-violet-600 text-violet-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'output' ? (
              <span className="flex items-center gap-1">
                Output
                {generated && <span className="w-2 h-2 bg-green-500 rounded-full inline-block"></span>}
              </span>
            ) : 'Configuration'}
          </button>
        ))}
      </div>
      {activeTab === 'generator' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Site Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Icon name="Globe" size={16} className="text-violet-500" />
              Site Information
            </h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Site Name</label>
              <input
                type="text"
                value={config?.siteName}
                onChange={e => updateConfig('siteName', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Site Description</label>
              <textarea
                value={config?.siteDescription}
                onChange={e => updateConfig('siteDescription', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Site URL</label>
              <input
                type="text"
                value={config?.siteUrl}
                onChange={e => updateConfig('siteUrl', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Language</label>
              <select
                value={config?.language}
                onChange={e => updateConfig('language', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                {['en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'zh', 'ar']?.map(l => (
                  <option key={l} value={l}>{l?.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>

          {/* AI Policy */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Icon name="Bot" size={16} className="text-violet-500" />
              AI Usage Policy
            </h3>
            <div className="space-y-3">
              {[
                { key: 'allowTraining', label: 'Allow AI Training', desc: 'Permit AI models to train on your content' },
                { key: 'allowIndexing', label: 'Allow AI Indexing', desc: 'Allow AI search engines to index your content' },
                { key: 'includePages', label: 'Include Page Index', desc: 'List all published pages in llm.txt' },
                { key: 'includeSitemap', label: 'Reference Sitemap', desc: 'Include link to sitemap.xml' },
                { key: 'includeContact', label: 'Include Contact Info', desc: 'Add contact email for AI attribution' },
              ]?.map(({ key, label, desc }) => (
                <label key={key} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config?.[key]}
                    onChange={e => updateConfig(key, e?.target?.checked)}
                    className="w-4 h-4 text-violet-600 rounded mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-800">{label}</div>
                    <div className="text-xs text-gray-500">{desc}</div>
                  </div>
                </label>
              ))}
            </div>
            {config?.includeContact && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={config?.contactEmail}
                  onChange={e => updateConfig('contactEmail', e?.target?.value)}
                  placeholder="contact@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Preferred Citation Format</label>
              <input
                type="text"
                value={config?.preferredCitation}
                onChange={e => updateConfig('preferredCitation', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Custom Instructions for AI</label>
              <textarea
                value={config?.customInstructions}
                onChange={e => updateConfig('customInstructions', e?.target?.value)}
                rows={3}
                placeholder="e.g. When referencing this site, always mention it is a CMS platform..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              />
            </div>
          </div>
        </div>
      )}
      {activeTab === 'output' && (
        <div className="space-y-4">
          {!generated ? (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
              <Icon name="Bot" size={40} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-4">Configure your settings and click <strong>Generate llm.txt</strong> to create your file</p>
              <Button variant="primary" iconName="Zap" iconPosition="left" onClick={generateLlmTxt} disabled={generating}>
                {generating ? 'Generating...' : 'Generate Now'}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between bg-gray-800 px-4 py-2 rounded-t-lg">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 font-mono">llm.txt</span>
                  <span className="text-xs text-violet-400">— {pages?.length} pages indexed</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                  >
                    <Icon name={copied ? 'Check' : 'Copy'} size={12} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                  >
                    <Icon name="Download" size={12} /> Download
                  </button>
                </div>
              </div>
              <pre className="p-4 text-xs text-gray-300 bg-gray-900 rounded-b-lg overflow-x-auto max-h-96 font-mono leading-relaxed whitespace-pre-wrap">
                {llmContent}
              </pre>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-green-700">{pages?.length}</div>
                  <div className="text-xs text-green-600">Pages Indexed</div>
                </div>
                <div className="bg-violet-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-violet-700">{llmContent?.split('\n')?.length}</div>
                  <div className="text-xs text-violet-600">Lines Generated</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-blue-700">{(new Blob([llmContent])?.size / 1024)?.toFixed(1)}KB</div>
                  <div className="text-xs text-blue-600">File Size</div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <Icon name="Info" size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Deploy this file to <strong>{config?.siteUrl}/llm.txt</strong> on your server. AI crawlers like GPTBot and PerplexityBot will automatically discover and read it.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LLMTxtGenerator;
